/* src/ui/windows/marketWindow.ts — trade, and see what you own (TASKS.md 3.7, 3.4).
 *
 * Two tabs over one window: Market (all 137 commodities, buy/sell) and Inventory (the player's
 * stores and the cart's stock, side by side).
 *
 * The list is filtered rather than paginated, and only the visible rows are built. 137 rows of
 * live-updating DOM next to a WebGL canvas is the kind of thing that quietly costs frames, and
 * the player is looking for one commodity at a time anyway.
 *
 * Every trade round-trips through the server and replaces the whole state, so the numbers on
 * screen after a buy are the server's, not an optimistic guess. The spread guarantees a buy
 * followed by a sell loses money — the UI says so up front rather than letting the player
 * discover it by watching their balance drop.
 */

import { gameStore } from '../../core/store.ts';
import type { CatalogItem, GameState, MarketEntry } from '../../core/types.ts';
import { buyItem, fetchItemsCatalog, sellItem } from '../../net/gameApi.ts';
import { incomeInterpolator } from '../../sim/incomeInterpolator.ts';
import { registerAction } from '../dispatch.ts';
import { el, text } from '../dom.ts';
import { formatMoney } from '../format.ts';
import { inventoryFromRecord, renderInventoryGrid } from '../panels/inventoryGrid.ts';
import { toast } from '../toast.ts';
import type { WindowManager } from './windowChrome.ts';

const WINDOW_ID = 'market';
/** Rows rendered at once. The filter is how you reach the rest. */
const MAX_ROWS = 40;

let catalog: ReadonlyMap<string, CatalogItem> = new Map();

function pctClass(pct: number): string {
  if (pct > 0.05) return 'gf-tick--up';
  if (pct < -0.05) return 'gf-tick--down';
  return 'gf-tick--flat';
}

export function openMarketWindow(windows: WindowManager, focusItem?: string): void {
  const existing = windows.get(WINDOW_ID);
  if (existing) {
    existing.focus();
    if (focusItem !== undefined) {
      const input = existing.el.querySelector<HTMLInputElement>('.gf-mkt-filter');
      if (input) {
        input.value = focusItem;
        input.dispatchEvent(new Event('input'));
      }
    }
    return;
  }

  let tab: 'market' | 'inventory' = 'market';
  let filter = focusItem ?? '';
  let qty = 10;

  const list = el('div', { class: 'gf-mkt-list' });
  const status = el('p', { class: 'gf-mkt-status' });
  const cashLabel = text('');

  const filterInput = el('input', {
    class: 'gf-mkt-filter',
    attrs: { type: 'search', placeholder: 'Filter commodities…', 'aria-label': 'Filter' },
    on: {
      input: (e) => {
        filter = (e.target as HTMLInputElement).value.trim().toUpperCase();
        paint(gameStore.get());
      },
    },
  });
  filterInput.value = filter;

  const qtyInput = el('input', {
    class: 'gf-mkt-qty',
    attrs: { type: 'number', min: '1', step: '1', 'aria-label': 'Quantity' },
    on: {
      input: (e) => {
        const v = Number((e.target as HTMLInputElement).value);
        qty = Number.isFinite(v) && v > 0 ? v : 1;
      },
    },
  });
  qtyInput.value = String(qty);

  const tabBar = el(
    'div',
    { class: 'gf-mkt-tabs' },
    el('button', { class: 'gf-mkt-tab is-active', attrs: { type: 'button' },
      data: { action: 'market:tab', tab: 'market' } }, 'Market'),
    el('button', { class: 'gf-mkt-tab', attrs: { type: 'button' },
      data: { action: 'market:tab', tab: 'inventory' } }, 'Inventory'),
  );

  const win = windows.open({ id: WINDOW_ID, title: 'Market', width: 400 });
  win.body.replaceChildren(
    tabBar,
    el(
      'div',
      { class: 'gf-mkt-controls' },
      filterInput,
      el('label', { class: 'gf-mkt-qty-label' }, 'Qty', qtyInput),
    ),
    el('p', { class: 'gf-mkt-cash' }, cashLabel),
    status,
    list,
  );

  function marketRow(entry: MarketEntry): HTMLElement {
    return el(
      'div',
      { class: 'gf-mkt-row' },
      el(
        'div',
        { class: 'gf-mkt-id' },
        el('span', { class: 'gf-mkt-key' }, entry.key),
        el('span', { class: 'gf-mkt-name' }, entry.name),
      ),
      el('span', { class: 'gf-mkt-price' }, formatMoney(entry.price)),
      el('span', { class: `gf-mkt-pct ${pctClass(entry.trendPct)}` },
        `${entry.trendPct > 0 ? '+' : ''}${entry.trendPct.toFixed(1)}%`),
      el('button', { class: 'gf-mkt-buy', attrs: { type: 'button' },
        data: { action: 'market:buy', itemId: entry.key } }, 'Buy'),
      el('button', { class: 'gf-mkt-sell', attrs: { type: 'button' },
        data: { action: 'market:sell', itemId: entry.key } }, 'Sell'),
    );
  }

  function paint(state: GameState | null): void {
    if (!state) return;
    cashLabel.textContent = `Cash ${formatMoney(state.cash)}`;

    if (tab === 'inventory') {
      const cart = state.cart;
      // An unplaced cart is carried, not stocked: it belongs in the bag beside the ingredients,
      // and the "on your cart" section would be an empty grid telling you to restock a cart
      // that is not anywhere yet.
      const inBag = cart !== null && !cart.placed;
      list.replaceChildren(
        el('h4', { class: 'gf-mkt-subhead' }, 'Your stores'),
        ...(inBag
          ? [
              el(
                'div',
                { class: 'gf-bag-cart' },
                el('span', { class: 'gf-bag-icon' }, '🛒'),
                el(
                  'span',
                  { class: 'gf-bag-id' },
                  el('b', null, cart.name),
                  el('em', null, 'Click a footpath on the map to set it down.'),
                ),
              ),
            ]
          : []),
        renderInventoryGrid(state.inventory, catalog, {
          emptyText: 'Your stores are empty — buy on the Market tab.',
        }),
        ...(inBag
          ? []
          : [
              el('h4', { class: 'gf-mkt-subhead' }, cart ? `On ${cart.name}` : 'On your cart'),
              renderInventoryGrid(cart ? inventoryFromRecord(cart.inventory) : [], catalog, {
                emptyText: 'The cart is out of stock — it will not sell until restocked.',
              }),
            ]),
      );
      return;
    }

    const rows = state.market
      .filter((m) => filter === '' || m.key.includes(filter) || m.name.toUpperCase().includes(filter))
      .slice(0, MAX_ROWS);

    list.replaceChildren(
      ...(rows.length === 0
        ? [el('p', { class: 'gf-mkt-status' }, `Nothing matches “${filter}”.`)]
        : rows.map(marketRow)),
    );
  }

  async function trade(kind: 'buy' | 'sell', itemId: string): Promise<void> {
    const cart = gameStore.get()?.cart;
    status.textContent = `${kind === 'buy' ? 'Buying' : 'Selling'} ${String(qty)} ${itemId}…`;
    try {
      // Ingredients go straight onto the cart so a restock actually feeds the sim; anything
      // else lands in the player's own stores.
      const isIngredient = cart?.recipe.some((r) => r.itemId === itemId) ?? false;
      const target = isIngredient && cart ? cart.id : undefined;
      const next =
        kind === 'buy' ? await buyItem(itemId, qty, target) : await sellItem(itemId, qty, target);
      incomeInterpolator.sync(next);
      gameStore.set(next);
      status.textContent = `${kind === 'buy' ? 'Bought' : 'Sold'} ${String(qty)} ${itemId}${
        isIngredient ? ' (to cart)' : ''
      }.`;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Trade failed.';
      status.textContent = msg;
      toast(msg);
    }
  }

  const unsubscribe = gameStore.subscribe(paint);
  const offTab = registerAction('market:tab', (_e, target) => {
    const next = target.dataset['tab'];
    if (next !== 'market' && next !== 'inventory') return;
    tab = next;
    for (const b of tabBar.querySelectorAll('.gf-mkt-tab')) {
      b.classList.toggle('is-active', (b as HTMLElement).dataset['tab'] === next);
    }
    paint(gameStore.get());
  });
  const offBuy = registerAction('market:buy', (_e, t) => {
    const id = t.dataset['itemId'];
    if (id !== undefined) void trade('buy', id);
  });
  const offSell = registerAction('market:sell', (_e, t) => {
    const id = t.dataset['itemId'];
    if (id !== undefined) void trade('sell', id);
  });

  // The catalog is static; fetch once, then repaint so names replace bare ids.
  void fetchItemsCatalog()
    .then((items) => {
      catalog = new Map(items.map((i) => [i.id, i]));
      paint(gameStore.get());
    })
    .catch(() => {
      /* Names degrade to ids; the market still trades. */
    });

  paint(gameStore.get());

  const close = win.close.bind(win);
  win.close = (): void => {
    unsubscribe();
    offTab();
    offBuy();
    offSell();
    close();
  };
}
